import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Modal } from '@companycam/slab-web';
import styled from 'styled-components';
import { useStripe, useElements } from '@stripe/react-stripe-js';

import { useGetStripeClientSecret } from './useGetStripeClientSecret';
import useSubscriptionPlanUpdate from 'components/billing/v2/billingCycle/useSubscriptionPlanUpdate';
import useDetailedBillingQuery from 'components/billing/v2/useDetailedBillingQuery';
import SubscriptionCycleInput from './SubscriptionCycleInput';
import toast from 'helpers/toast';
import { StripeError } from '@stripe/stripe-js';
import { MONTHLY } from '../Constants';
import { usePrevious } from 'javascripts/hooks';
import PlanPaymentInfo from './PlanPaymentInfo';
import usePaymentSourceInfoQuery from './usePaymentSourceInfoQuery';
import { translate } from 'javascripts/translations/i18n';
import {
  BillingIndentifier,
  BillingQueryResponse,
  PaymentMethodFormIdentifier,
} from '../types';
import PaymentSourceForm from './PaymentSourceForm';
import Timeline from './Timeline';
import {
  ADD_PAYMENT_MODAL,
  TRIAL_MODAL,
  UPDATE_PAYMENT_MODAL,
} from './Constants';
import SubmitPaymentButton from './SubmitPaymentButton';

export type StripePaymentMethodModalProps = {
  formId: PaymentMethodFormIdentifier;
  onRequestClose: () => void;
  redirect?: string;
  subscriptionCycle: string;
  selectedSubscriptionPlan: BillingIndentifier;
  paymentModalType: PaymentMethodFormIdentifier;
  onPaymentSubmitted?: () => void;
  showCurrentPaymentMethod?: boolean;
};

const styles = {
  modalBodyStyleWide: {
    backgroundColor: 'var(--cc_color_background_2)',
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
  },
  modalBodyStyleSlim: {
    backgroundColor: 'var(--cc_color_background_2)',
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
    width: '100%',
  },
};

const PaymentButtonContainer = styled.div`
  margin-top: var(--cc_size_spacing_l);
  width: 100%;
`;

const PaymentInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  flex: 3;
  align-content: flex-start;
  justify-content: space-between;
`;

const ModalContentContainer = styled.div<{ fullWidth: boolean }>`
  display: flex;
  flex-direction: row;
  @media (max-width: 1000px) {
    flex-direction: column;
  }
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'unset')};
`;

function StripePaymentMethodModal({
  formId,
  onRequestClose,
  paymentModalType,
  onPaymentSubmitted,
  redirect,
  subscriptionCycle = MONTHLY,
  selectedSubscriptionPlan,
  showCurrentPaymentMethod = true,
}: StripePaymentMethodModalProps) {
  const [paymentSubmitLoading, setPaymentSubmitLoading] = useState(false);
  const [formFilled, setFormFilled] = useState(false);
  const [isWalletPayment, setIsWalletPayment] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');
  const [selectedSubscriptionCycle, setSelectedSubscriptionCycle] =
    useState(subscriptionCycle);

  const stripe = useStripe();
  const elements = useElements();

  const { data: result, isLoading: stripeLoading } = useGetStripeClientSecret();
  const {
    availableBillingPlans,
    currentPlan,
    upcomingInvoice,
    loading: billingLoading,
    subscription: currentSubscription,
  }: BillingQueryResponse = useDetailedBillingQuery();

  const { paymentSource, loading: loadingPaymentSource } =
    usePaymentSourceInfoQuery();
  const [showPaymentSource, setShowPaymentSource] = useState(false);

  const subscriptionPlan = selectedSubscriptionPlan || currentPlan?.identifier;

  const selectedPlan = useMemo(() => {
    if (billingLoading) return null;
    const subscription = availableBillingPlans.find(
      (plan) => plan.identifier === subscriptionPlan,
    );

    return subscription;
  }, [availableBillingPlans, billingLoading, subscriptionPlan]);

  const handleStripeFailure = useCallback(
    (stripeError?: { error: StripeError }) => {
      if (stripeError) {
        console.error(stripeError.error);
      }
      toast.error(translate('billing.stripePaymentModal.billingError'), 5000);
      if (redirect) {
        window.Turbo.visit(redirect);
      } else {
        onRequestClose();
      }
    },
    [onRequestClose, redirect],
  );

  const selectedSubscription = useMemo(() => {
    if (billingLoading) return null;
    else if (!selectedPlan) {
      handleStripeFailure();
    }
    const { node: newCustomerSubscription } =
      selectedPlan.subscriptionOptions.edges.find(
        (option) => option.node.cycle === selectedSubscriptionCycle,
      );
    return newCustomerSubscription;
  }, [
    billingLoading,
    handleStripeFailure,
    selectedPlan,
    selectedSubscriptionCycle,
  ]);

  const onComplete = useCallback(() => {
    if (redirect) {
      window.Turbo.visit(redirect);
    }
    onPaymentSubmitted?.();
    onRequestClose();
  }, [onRequestClose, redirect, onPaymentSubmitted]);

  const { subscriptionPlanUpdate, loading: subscriptionUpdateLoading } =
    useSubscriptionPlanUpdate({
      showToast: false,
    });

  const savePaymentMethod = useCallback(async () => {
    const savePaymentResponse = await stripe.confirmSetup({
      elements,
      clientSecret: result.data.clientSecret,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    });

    if (savePaymentResponse.error) {
      setSubmitErrorMessage(savePaymentResponse.error.message);
      setPaymentSubmitLoading(false);
    } else {
      toast.success(translate('billing.stripePaymentModal.paymentSaved'));
    }
  }, [elements, result, stripe]);

  const formIsReady = stripe && elements && !stripeLoading;

  const paymentMethodExists = formFilled || !!paymentSource;

  const submitPaymentDisabled =
    !formIsReady ||
    !paymentMethodExists ||
    subscriptionUpdateLoading ||
    paymentSubmitLoading ||
    billingLoading ||
    (!showPaymentSource && !formFilled);

  const prevSubscriptionUpdateLoading = usePrevious(subscriptionUpdateLoading);

  useEffect(() => {
    if (!loadingPaymentSource && !!paymentSource && showCurrentPaymentMethod) {
      setShowPaymentSource(true);
    }
  }, [loadingPaymentSource, paymentSource, showCurrentPaymentMethod]);

  useEffect(() => {
    if (
      prevSubscriptionUpdateLoading &&
      !subscriptionUpdateLoading &&
      !submitErrorMessage
    ) {
      // need to wait for stripe to update billing info before redirecting
      onComplete();
    }
  }, [
    onComplete,
    prevSubscriptionUpdateLoading,
    submitErrorMessage,
    subscriptionUpdateLoading,
  ]);

  const handleSubmit = useCallback(async () => {
    setPaymentSubmitLoading(true);
    setSubmitErrorMessage('');

    if (!showPaymentSource) {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setSubmitErrorMessage(submitError.message);
        setPaymentSubmitLoading(false);
        return;
      }

      await savePaymentMethod();
    }

    await subscriptionPlanUpdate(selectedSubscription);
  }, [
    elements,
    savePaymentMethod,
    selectedSubscription,
    showPaymentSource,
    subscriptionPlanUpdate,
  ]);

  return (
    <Modal
      isOpen
      title=""
      onClose={onRequestClose}
      size={paymentModalType === UPDATE_PAYMENT_MODAL ? 'small' : 'medium'}
      bodyStyle={
        paymentModalType === UPDATE_PAYMENT_MODAL
          ? styles.modalBodyStyleSlim
          : styles.modalBodyStyleWide
      }
      headerStyle={{
        borderBottom: 'none',
        backgroundColor: 'var(--cc_color_background_2)',
      }}
    >
      <ModalContentContainer
        fullWidth={paymentModalType === UPDATE_PAYMENT_MODAL}
        id="stripe-payment-modal"
      >
        <PaymentInfoContainer>
          {paymentModalType === TRIAL_MODAL ? (
            <SubscriptionCycleInput
              selectedSubscriptionCycle={selectedSubscriptionCycle}
              handleOnChange={setSelectedSubscriptionCycle}
            />
          ) : null}
          <PaymentSourceForm
            paymentSource={paymentSource}
            loadingPaymentSource={loadingPaymentSource}
            setFormFilled={setFormFilled}
            setIsWalletPayment={setIsWalletPayment}
            showPaymentSource={showPaymentSource}
            setShowPaymentSource={setShowPaymentSource}
            handleSubmit={handleSubmit}
            handleStripeFailure={handleStripeFailure}
          />
          {paymentModalType === UPDATE_PAYMENT_MODAL ? (
            <PaymentButtonContainer>
              <SubmitPaymentButton
                cta={translate(
                  'billing.v2.currentPaymentInfo.updatePaymentMethod',
                )}
                handleSubmit={handleSubmit}
                formId={formId}
                submitErrorMessage={submitErrorMessage}
                submitPaymentDisabled={submitPaymentDisabled}
                isWalletPayment={isWalletPayment}
                paymentSubmitLoading={paymentSubmitLoading}
              />
            </PaymentButtonContainer>
          ) : null}
        </PaymentInfoContainer>
        {paymentModalType === TRIAL_MODAL ? (
          <Timeline
            billingLoading={billingLoading}
            handleSubmit={handleSubmit}
            formId={formId}
            isWalletPayment={isWalletPayment}
            paymentSubmitLoading={paymentSubmitLoading}
            planTitle={selectedPlan?.title}
            selectedSubscription={selectedSubscription}
            submitErrorMessage={submitErrorMessage}
            submitPaymentDisabled={submitPaymentDisabled}
          />
        ) : null}
        {paymentModalType === ADD_PAYMENT_MODAL ? (
          <PlanPaymentInfo
            billingLoading={billingLoading}
            handleSubmit={handleSubmit}
            formId={formId}
            isWalletPayment={isWalletPayment}
            paymentSubmitLoading={paymentSubmitLoading}
            planTitle={selectedPlan?.title}
            amountDue={upcomingInvoice?.amountDue}
            selectedSubscriptionCycle={
              currentSubscription?.subscriptionOption?.cycle
            }
            submitErrorMessage={submitErrorMessage}
            submitPaymentDisabled={submitPaymentDisabled}
          />
        ) : null}
      </ModalContentContainer>
    </Modal>
  );
}

export default StripePaymentMethodModal;
